import React, {ReactElement, ReactNode, RefObject} from 'react';
import styled from 'styled-components';
import {theme} from '../style';
import {blueDownArrow} from './index';

export type SimpleDropdownOption = {
  id: number | string;
  name: ReactNode;
};

export type SimpleDropdownProps = {
  selections: SimpleDropdownOption[];
  onSelect: (s: SimpleDropdownOption) => void;
  value?: SimpleDropdownOption;
  // pass a placeholder and we'll display it, otherwise blank
  placeholder?: ReactNode;
  // how wide, in pixels? default is 200
  width?: number;
  // pass a custom arrow to use
  arrow?: (open: boolean, onShowDropdown: () => void) => ReactElement;
  // custom height for dropdown, defaults to 200px
  dropdownHeight?: number;
};

type SimpleDropdownState = {
  open: boolean;
};

const height = 32;
const defaultDropdownHeight = 200;
const defaultWidth = 200;

export default class SimpleDropdown extends React.Component<
  SimpleDropdownProps,
  SimpleDropdownState
> {
  state: SimpleDropdownState = {
    open: false,
  };

  private readonly ref: RefObject<HTMLDivElement>;

  constructor(props: SimpleDropdownProps) {
    super(props);

    this.ref = React.createRef<HTMLDivElement>();

    this.onChange = this.onChange.bind(this);
    this.onShowDropdown = this.onShowDropdown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside, true);
  }

  onShowDropdown() {
    this.setState({open: !this.state.open});
  }

  handleClickOutside(event: MouseEvent) {
    if (event.target instanceof HTMLElement && !this.ref.current?.contains(event.target)) {
      this.setState({open: false});
    }
  }

  onChange(i: SimpleDropdownOption) {
    this.setState({
      open: false,
    });

    this.props.onSelect(i);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
  }

  render() {
    const {
      selections,
      placeholder,
      width = defaultWidth,
      value,
      arrow = (open: boolean, onShowDropdown: () => void) => (
        <DropdownIcon
          viewBox={'0 0 330 330'}
          transform={open ? 'rotate(180)' : undefined}
          onClick={onShowDropdown}>
          {blueDownArrow}
        </DropdownIcon>
      ),
      dropdownHeight = defaultDropdownHeight,
    } = this.props;
    const {open} = this.state;

    const displayPlaceholder = !value;

    return (
      <Wrapper ref={this.ref} width={width} onClick={this.onShowDropdown}>
        <Value className={displayPlaceholder ? 'placeholder' : undefined}>
          {displayPlaceholder ? placeholder || '' : value!.name}
        </Value>
        {arrow(open, this.onShowDropdown)}

        {open && selections.length > 0 && (
          <Dropdown width={width} dropdownHeight={dropdownHeight}>
            <UnstyledList>
              {selections.map((i, j) => (
                <DropdownItem key={j} onClick={() => this.onChange(i)}>
                  {i.name}
                </DropdownItem>
              ))}
            </UnstyledList>
          </Dropdown>
        )}
      </Wrapper>
    );
  }
}

const Wrapper = styled.div<{width: number}>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: ${({width}) => width + 'px'};
  font-family: ${theme.fonts.primaryFontFamily};
  color: ${theme.colors.basicallyBlack};
  border: 1px solid ${theme.colors.chartLineSecondary};
  border-radius: 3px;
  padding: 1px;
  cursor: pointer;
`;

const Value = styled.div`
  flex: 1;
  padding: 0 5px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &.placeholder {
    color: ${theme.colors.chartLinePrimary};
  }
`;

const DropdownIcon = styled.svg`
  height: ${height - 4}px;
  fill: ${theme.colors.primaryBlue};
  cursor: pointer;
  flex-shrink: 0;
`;

const UnstyledList = styled.ul`
  padding: 0;
  margin: 5px 0 0 0;
  list-style-type: none;
`;

const DropdownRow = styled.li`
  padding-bottom: 10px;
  padding: 5px;
  transition: 100ms ease-out;
`;

const DropdownItem = styled(DropdownRow)`
  cursor: pointer;

  :hover {
    background-color: ${theme.colors.chartBackground};
  }
`;

const Dropdown = styled.div<{width: number; dropdownHeight: number}>`
  position: absolute;
  top: ${height + 5}px;
  left: 0;
  max-height: ${({dropdownHeight}) => dropdownHeight + 'px'};
  width: ${({width}) => width + 'px'};
  overflow-x: hidden;
  border: 1px solid ${theme.colors.chartLineSecondary};
  background: #fcfcfc;
  z-index: 1;
`;
